import Cookies from 'js-cookie';
import {
  clearGclid,
  CONSENT_COOKIE,
  CONSENT_EVENT,
  readStoredConsent,
  type StoredConsentInterface,
} from '@sc/shared-ui';

export { CONSENT_COOKIE, CONSENT_EVENT };

export type ConsentStateInterface = StoredConsentInterface;

export const OPEN_CONSENT_EVENT = 'sc-open-cookie-settings';

const MAX_AGE_DAYS = 365;

export const defaultDenied = (): ConsentStateInterface => ({
  necessary: true,
  preferences: false,
  statistics: false,
  marketing: false,
});

export const allowAll = (): ConsentStateInterface => ({
  necessary: true,
  preferences: true,
  statistics: true,
  marketing: true,
});

export const readConsent = readStoredConsent;

const grant = (on: boolean): 'denied' | 'granted' =>
  on ? 'granted' : 'denied';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

export const updateGoogleConsent = (consent: ConsentStateInterface): void => {
  window.gtag?.('consent', 'update', {
    ad_storage: grant(consent.marketing),
    ad_user_data: grant(consent.marketing),
    ad_personalization: grant(consent.marketing),
    analytics_storage: grant(consent.statistics),
    functionality_storage: grant(consent.preferences),
    personalization_storage: grant(consent.preferences),
    security_storage: 'granted',
  });

  window.gtag?.('set', 'ads_data_redaction', !consent.marketing);
};

const expireCookie = (name: string): void => {
  document.cookie = `${name}=; Max-Age=0; path=/`;
};

// Consent Mode stops new writes. Drop copies already on this host when the
// visitor withdraws the category that allowed them.
const clearWithdrawnCookies = (consent: ConsentStateInterface): void => {
  if (!consent.marketing) clearGclid();

  if (typeof document === 'undefined') return;

  const names = document.cookie
    .split(';')
    .map((part) => part.split('=')[0]?.trim())
    .filter((name): name is string => !!name);

  for (const name of names) {
    const statistics =
      name === '_ga' ||
      name.startsWith('_ga_') ||
      name.startsWith('mp_');

    const marketing =
      name === '_gcl_au' || name === '_gcl_ls' || name === 'IDE';

    if (
      (statistics && !consent.statistics) ||
      (marketing && !consent.marketing)
    ) {
      expireCookie(name);
    }
  }
};

export const saveConsent = (consent: ConsentStateInterface): void => {
  Cookies.set(CONSENT_COOKIE, JSON.stringify(consent), {
    path: '/',
    expires: MAX_AGE_DAYS,
    sameSite: 'lax',
    secure: window.location.protocol === 'https:',
  });

  updateGoogleConsent(consent);

  clearWithdrawnCookies(consent);

  window.dispatchEvent(new Event(CONSENT_EVENT));
};

export const openCookieSettings = (): void => {
  window.dispatchEvent(new Event(OPEN_CONSENT_EVENT));
};
